import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Spinner,
  addToast,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  useDisclosure,
  Input
} from "@heroui/react";
import {
  BAFStakingListApi,
  BAFTokenStaking,
  writeContractApi
} from "../../../api/methods-marketplace";
import { useWallet } from "../../../context/WalletContext";
import bafABI from "../../../abis/baf-abi.json";
import { useSelector } from "react-redux";
import {
  readContract,
  waitForTransactionReceipt,
  writeContract
} from "@wagmi/core";
import { ethers } from "ethers";
import { rainbowKitConfig } from "../../../utils/config";
import stakingABI from "../../../abis/baf-token-staking-abi.json";
import { useNavigate } from "react-router-dom";
import { isUserLoggedIn } from "../../../redux/reducer/user_reducer";
import { encodeFunctionData } from "viem";

const StakingTable = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [bafPlanList, setBAFPlanList] = useState([]);
  const [stakingPlan, setStakingPlan] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [stakingAmount, setStakingAmount] = useState("");
  const [isStaking, setIsStaking] = useState(false);
  const {
    isConnected,
    address,
    hasAddressMismatch,
    refreshBalance,
    isCustodialWallet,
    publicClient
  } = useWallet();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const user = useSelector((state) => state.user);
  const isLoggedIn = useSelector(isUserLoggedIn);
  const navigate = useNavigate();

  // Constants for contract addresses
  const BAF_STAKING_CONTRACT_ADDRESS =
    process.env.REACT_APP_BAF_STAKING_CONTRACT_ADDRESS;
  const BAF_TOKEN_CONTRACT_ADDRESS = process.env.REACT_APP_BAF_TOKEN_ADDRESS;

  const tableColumns = [
    { uid: "name", label: "NFT Plans" },
    { uid: "duration", label: "Duration" },
    { uid: "interest", label: "ROI" },
    { uid: "action", label: "Action" }
  ];

  const fetchBAFListPlan = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await BAFStakingListApi();
      if (response?.data?.data) {
        setBAFPlanList(response.data.data);
      }
    } catch (error) {
      console.error("Failed to fetch BAF plan list:", error);
      addToast({
        title: "Error",
        description: "Failed to load staking plans. Please try again later.",
        color: "danger",
        variant: "bordered"
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setIsLoading(false);
      return;
    }
    fetchBAFListPlan();
  }, [isLoggedIn, fetchBAFListPlan]);

  const handleOpenStakeModal = useCallback(
    (plan) => {
      if (!isLoggedIn) {
        addToast({
          title: "Login Required",
          description: "Please log in to your account to stake.",
          color: "warning",
          variant: "bordered"
        });
        return;
      }

      // Custodial wallet flow
      if (isCustodialWallet) {
        if (user?.data?.address) {
          setStakingPlan(plan.name);
          setSelectedPlan(plan);
          onOpen();
        } else {
          addToast({
            title: "Profile Incomplete",
            description: "Please complete your profile setup first.",
            color: "warning",
            variant: "bordered"
          });
        }
        return;
      }

      // Non-custodial wallet flow
      if (!isConnected || !address) {
        addToast({
          title: "Not Connected",
          description: "Please connect your wallet first.",
          color: "danger",
          variant: "bordered"
        });
        return;
      }

      if (hasAddressMismatch) {
        addToast({
          title: "Address Mismatch",
          description:
            "The connected wallet address does not match your registered address. Please reconnect with the correct one.",
          color: "warning",
          variant: "bordered"
        });
        return;
      }

      if (!user?.data?.address) {
        addToast({
          title: "Profile Incomplete",
          description:
            "Wallet address belongs to another account. Please reconnect with the correct one.",
          color: "warning",
          variant: "bordered"
        });
        return;
      }

      // All validations passed - open modal
      setStakingPlan(plan.name);
      setSelectedPlan(plan);
      onOpen();
    },
    [
      address,
      onOpen,
      user,
      isConnected,
      hasAddressMismatch,
      isLoggedIn,
      isCustodialWallet
    ]
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedPlan(null);
      setStakingAmount("");
    }
  }, [isOpen]);

  const handleStakeAmountChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setStakingAmount(value);
    }
  };

  const handleInitBAFStake = async () => {
    console.log("initiated");
    if (!isCustodialWallet) {
      if (!isConnected || !address) {
        addToast({
          title: "Not Connected",
          description: "Please connect your wallet first....",
          color: "danger",
          variant: "bordered"
        });
        return;
      }

      if (
        (!user?.data?.address && address) ||
        (hasAddressMismatch && isCustodialWallet)
      ) {
        addToast({
          title: hasAddressMismatch ? "Address Mismatch" : "Profile Incomplete",
          description: hasAddressMismatch
            ? "The connected wallet address does not match your registered address. Please reconnect with the correct one."
            : "Wallet address belongs to another account. Please reconnect with the correct one.",
          color: "danger",
          variant: "bordered"
        });
        return;
      }
    }

    if (!stakingAmount || !selectedPlan) {
      addToast({
        title: "Invalid Input",
        description: "Please enter a valid staking amount",
        color: "danger",
        variant: "bordered"
      });
      return;
    }

    try {
      setIsStaking(true);
      if (isCustodialWallet) {
        if (!publicClient) {
          addToast({
            title: "PublicClient failed",
            description: "publicClient is required for custodial wallet",
            color: "warning",
            variant: "bordered"
          });
        }

        const balance = await publicClient.readContract({
          address: BAF_TOKEN_CONTRACT_ADDRESS,
          abi: bafABI,
          functionName: "balanceOf",
          args: [address]
        });

        const decimals = await publicClient.readContract({
          address: BAF_TOKEN_CONTRACT_ADDRESS,
          abi: bafABI,
          functionName: "decimals"
        });

        const rawStakingAmount = ethers.parseUnits(
          stakingAmount?.toString(),
          decimals
        );

        if (balance < rawStakingAmount) {
          addToast({
            title: "Insufficient Balance",
            description:
              "You don't have enough BAF tokens to stake this amount.",
            color: "danger",
            variant: "bordered"
          });
          return;
        }

        const allowance = await publicClient.readContract({
          address: BAF_TOKEN_CONTRACT_ADDRESS,
          abi: bafABI,
          functionName: "allowance",
          args: [address, BAF_STAKING_CONTRACT_ADDRESS]
        });

        if (allowance < rawStakingAmount) {
          const approveCallData = encodeFunctionData({
            abi: bafABI,
            functionName: "approve",
            args: [BAF_STAKING_CONTRACT_ADDRESS, rawStakingAmount]
          });

          const approveResult = await writeContractApi({
            address: BAF_TOKEN_CONTRACT_ADDRESS,
            encoded_data: approveCallData
          });

          if (!approveResult?.data?.tx_hash) {
            throw new Error("Approval transaction hash not received");
          }

          console.log("Approval tx hash:", approveResult.data.tx_hash);

          const approvalReceipt = await publicClient.waitForTransactionReceipt({
            hash: approveResult.data.tx_hash
          });

          console.log("Approval receipt:", approvalReceipt);

          if (approvalReceipt.status !== "success") {
            addToast({
              title: "Token approval transaction failed",
              color: "danger",
              variant: "bordered"
            });
            return;
          }
        }

        // Step 2: Stake tokens
        const stakeCallData = encodeFunctionData({
          abi: stakingABI,
          functionName: "stake",
          args: [rawStakingAmount, selectedPlan.id]
        });

        const stakeResult = await writeContractApi({
          address: BAF_STAKING_CONTRACT_ADDRESS,
          encoded_data: stakeCallData
        });

        if (!stakeResult?.data?.tx_hash) {
          throw new Error("Staking transaction hash not received");
        }

        const stakingTxHash = stakeResult.data.tx_hash;
        console.log("Staking tx hash:", stakingTxHash);

        // Wait for staking receipt
        const stakingReceipt = await publicClient.waitForTransactionReceipt({
          hash: stakingTxHash
        });

        console.log("Staking receipt:", stakingReceipt);

        if (stakingReceipt?.status !== "success") {
          addToast({
            title: "Stake transaction failed",
            color: "danger",
            variant: "bordered"
          });
          return;
        }

        // Update backend
        const data = {
          method: "token_stake",
          data: {
            plan: selectedPlan?.id,
            amount: stakingAmount,
            number_of_days: selectedPlan?.duration,
            hash: stakingTxHash
          }
        };

        await handleUpdateTokenStaking(data);

        addToast({
          title: "Staking Successful!",
          description: `Successfully staked ${stakingAmount} BAF tokens for ${selectedPlan?.duration} days.`,
          color: "success",
          variant: "bordered"
        });
      } else {
        const balance = await readContract(rainbowKitConfig, {
          address: BAF_TOKEN_CONTRACT_ADDRESS,
          abi: bafABI,
          functionName: "balanceOf",
          args: [address]
        });

        const decimals = await readContract(rainbowKitConfig, {
          address: BAF_TOKEN_CONTRACT_ADDRESS,
          abi: bafABI,
          functionName: "decimals"
        });

        const rawStakingAmount = ethers.parseUnits(
          stakingAmount?.toString(),
          decimals
        );

        if (balance < rawStakingAmount) {
          addToast({
            title: "Insufficient Balance",
            description:
              "You don't have enough BAF tokens to stake this amount.",
            color: "danger",
            variant: "bordered"
          });
          return;
        }

        const allowance = await readContract(rainbowKitConfig, {
          abi: bafABI,
          address: BAF_TOKEN_CONTRACT_ADDRESS,
          functionName: "allowance",
          args: [address, BAF_STAKING_CONTRACT_ADDRESS]
        });

        if (allowance < rawStakingAmount) {
          const approveTx = await writeContract(rainbowKitConfig, {
            abi: bafABI,
            address: BAF_TOKEN_CONTRACT_ADDRESS,
            functionName: "approve",
            args: [BAF_STAKING_CONTRACT_ADDRESS, rawStakingAmount]
          });

          const approvalReceipt = await waitForTransactionReceipt(
            rainbowKitConfig,
            {
              hash: approveTx,
              confirmations: 1
            }
          );

          if (approvalReceipt.status !== "success") {
            addToast({
              title: "Token approval transaction failed",
              color: "danger",
              variant: "bordered"
            });
            return;
          }
        }

        const stakingTx = await writeContract(rainbowKitConfig, {
          abi: stakingABI,
          address: BAF_STAKING_CONTRACT_ADDRESS,
          functionName: "stake",
          args: [rawStakingAmount, selectedPlan.id]
        });

        const stakingReceipt = await waitForTransactionReceipt(
          rainbowKitConfig,
          {
            hash: stakingTx,
            confirmations: 1
          }
        );

        if (stakingReceipt.status !== "success") {
          addToast({
            title: "Stake transaction failed",
            color: "danger",
            variant: "bordered"
          });
          return;
        }

        const data = {
          method: "token_stake",
          data: {
            plan: selectedPlan?.id,
            amount: stakingAmount,
            number_of_days: selectedPlan?.duration,
            hash: stakingTx
          }
        };

        await handleUpdateTokenStaking(data);

        addToast({
          title: "Staking Successful!",
          description: `Successfully staked ${stakingAmount} BAF tokens for ${selectedPlan?.duration} days.`,
          color: "success",
          variant: "bordered"
        });
      }
    } catch (error) {
      console.error("Staking initialization error:", error);

      const userRejected =
        error?.message?.includes("User rejected") ||
        error?.message?.includes("User denied");

      const message =
        error?.response?.data?.message ||
        error?.reason ||
        error?.shortMessage ||
        (error?.message?.includes("Already staked")
          ? "You've already staked this token."
          : userRejected
            ? "Transaction rejected by user."
            : "Transaction failed. Please try again.");

      addToast({
        title: userRejected ? "Transaction Rejected" : "Error",
        description: message,
        color: userRejected ? "warning" : "danger",
        variant: "bordered"
      });
    } finally {
      setStakingPlan(null);
      setIsStaking(false);
    }
  };

  const handleUpdateTokenStaking = async (data) => {
    try {
      const response = await BAFTokenStaking(data);
      if (response) {
        addToast({
          title: "Staking Successful",
          description: `You've successfully staked ${stakingAmount} BAF for ${selectedPlan.duration} days.`,
          color: "success",
          variant: "bordered"
        });
        setStakingPlan(null);
        await refreshBalance();
        onOpenChange();
        fetchBAFListPlan();
      }
    } catch (error) {
      setStakingPlan(null);
      console.log("🚀 ~ handleUpdateTokenStaking ~ error:", error);
    }
  };

  const renderCell = useCallback(
    (data, columnKey) => {
      const cellValue = data[columnKey];

      switch (columnKey) {
        case "name":
          return (
            <div className="flex flex-col">
              <p className="font-sans text-lg text-black font-semibold">
                {cellValue}
              </p>
            </div>
          );
        case "duration":
          return (
            <div className="flex flex-col">
              <p className="font-sans text-lg text-black font-normal">
                {cellValue}
              </p>
            </div>
          );
        case "interest":
          return (
            <div className="flex flex-col">
              <span className="font-sans text-lg text-black font-normal">
                {cellValue}
              </span>
            </div>
          );
        case "action":
          return (
            <div className="relative flex justify-end items-center gap-2">
              {data?.stake_token ? (
                <Button
                  className="bg-[#CCCDC6] border border-black text-base font-bold h-10 min-w-28"
                  radius="sm"
                  onPress={() => {
                    window.location.href =
                      "/profile?tab=staking&staking_type=baf";
                  }}
                >
                  Already Staked
                </Button>
              ) : (
                <Button
                  className="bg-yellowbuy border border-black text-base font-bold h-10 min-w-28"
                  radius="sm"
                  onPress={() => handleOpenStakeModal(data)}
                >
                  Stake
                </Button>
              )}
            </div>
          );
        default:
          return cellValue;
      }
    },
    [handleOpenStakeModal]
  );

  const classNames = useMemo(
    () => ({
      base: ["p-0"],
      th: [
        "bg-transparent",
        "text-black",
        "border-b",
        "border-[#C2C2C2]",
        "py-4",
        "px-4",
        "whitespace-nowrap",
        "text-lg",
        "font-normal"
      ],
      td: [
        "py-3",
        "px-4",
        "group-data-[first=true]/tr:first:before:rounded-none",
        "group-data-[first=true]/tr:last:before:rounded-none",
        "group-data-[middle=true]/tr:before:rounded-none",
        "group-data-[last=true]/tr:first:before:rounded-none",
        "group-data-[last=true]/tr:last:before:rounded-none"
      ]
    }),
    []
  );

  const renderContent = () => {
    if (!isLoggedIn) {
      return (
        <div className="flex flex-col items-center justify-center py-20 px-4">
          <div className="w-20 h-20 mb-6 rounded-full bg-gray-100 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-10 w-10 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">
            Login Required
          </h2>
          <p className="text-gray-600 text-center max-w-md mb-8">
            Please log in to access BAF token staking features and manage your
            staking plans.
          </p>
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
            <Button
              className="bg-yellowbuy border border-black rounded-lg font-medium px-6 py-2"
              onPress={() => navigate("/login")}
            >
              Login to Your Account
            </Button>
            <Button
              className="bg-white border border-gray-300 rounded-lg font-medium px-6 py-2"
              onPress={() => navigate("/sign-up")}
            >
              Create Account
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="z-0 flex flex-col relative justify-between gap-4 bg-content1 overflow-auto rounded-large w-full border border-[#C2C2C2]">
        <Table
          aria-label="Staking plans table"
          removeWrapper
          classNames={classNames}
        >
          <TableHeader columns={tableColumns}>
            {(column) => (
              <TableColumn
                key={column.uid}
                align={column.uid === "action" ? "end" : "start"}
              >
                {column.label}
              </TableColumn>
            )}
          </TableHeader>
          <TableBody
            emptyContent={"No staking plans available"}
            items={bafPlanList}
            isLoading={isLoading}
            loadingContent={
              <Spinner
                classNames={{
                  circle1: "!border-b-yellowbuy",
                  circle2: "!border-b-yellowbuy"
                }}
                label="Loading..."
              />
            }
          >
            {(item) => (
              <TableRow key={item?.id || item?.name}>
                {(columnKey) => (
                  <TableCell>{renderCell(item, columnKey)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <>
      {renderContent()}

      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        isDismissable={!isStaking}
        isKeyboardDismissDisabled={isStaking}
        size="sm"
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">
                {selectedPlan
                  ? `Stake BAF - ${selectedPlan.name}`
                  : "Token Staking"}
              </ModalHeader>
              <ModalBody className="py-0 pb-4 gap-4">
                <Input
                  label="Amount"
                  type="text"
                  labelPlacement="outside"
                  placeholder="Amount of BAF to stake"
                  value={stakingAmount}
                  onChange={handleStakeAmountChange}
                  classNames={{
                    label:
                      "text-sm font-medium text-[#6A6A6A] group-data-[filled-within=true]:text-[#6A6A6A]",
                    inputWrapper:
                      "h-11 !appearance-none block bg-transparent data-[hover=true]:bg-transparent group-data-[focus=true]:bg-transparent shadow-none w-full px-3 py-2 border border-[#C2C2C2] data-[hover=true]:border-[#C2C2C2] group-data-[focus=true]:border-[#C2C2C2] rounded-lg focus:outline-none",
                    input:
                      "text-base text-[#6A6A6A] placeholder-[#6A6A6A] !appearance-none font-normal"
                  }}
                />
                <Input
                  label="Plan"
                  type="text"
                  labelPlacement="outside"
                  placeholder="Plan"
                  value={selectedPlan?.name}
                  readOnly
                  classNames={{
                    inputWrapper: "h-11"
                  }}
                />
                <Input
                  label="Duration"
                  type="text"
                  labelPlacement="outside"
                  placeholder="Duration"
                  value={selectedPlan?.duration}
                  readOnly
                  classNames={{
                    inputWrapper: "h-11"
                  }}
                />
                <Input
                  label="ROI"
                  type="text"
                  labelPlacement="outside"
                  placeholder="ROI"
                  value={selectedPlan?.interest}
                  readOnly
                  classNames={{
                    inputWrapper: "h-11"
                  }}
                />
                <div className="flex justify-center mt-4">
                  <Button
                    color="primary"
                    className={`${
                      isStaking || !stakingAmount
                        ? "bg-[#D3D3D3]"
                        : "bg-yellowbuy"
                    } border border-black rounded-lg h-10 ${
                      isStaking || !stakingAmount ? "text-[#fff]" : "text-black"
                    } min-w-[12rem]`}
                    onPress={handleInitBAFStake}
                    isLoading={isStaking}
                    disabled={isStaking || !stakingAmount}
                  >
                    {isStaking ? "Processing..." : "Confirm Stake"}
                  </Button>
                </div>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default StakingTable;
